import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";

/**
 * InstallAppPrompt — a soft, dismissible "Install Memory Lane" toast.
 *
 * Behavior:
 *   • On Android/Chrome/Edge we listen for the standard `beforeinstallprompt`
 *     event and show our own styled banner (Chrome's default banner doesn't
 *     match the brand). When tapped we trigger Chrome's native prompt.
 *   • On iOS (which doesn't support `beforeinstallprompt`) we show a
 *     contextual "Tap the share icon → Add to Home Screen" tip card the
 *     first time a likely-caregiver session happens.
 *   • Dismissal is sticky for 14 days (localStorage flag). After install
 *     the banner never reappears.
 *
 * Mount this once at the App level — it self-positions in the bottom
 * corner and is invisible until the right moment.
 */

const DISMISS_KEY = "mlc_install_dismissed_until";
const DISMISS_DAYS = 14;
const ROUTE_HIDDEN = ["/login", "/book/success", "/onboarding"];

function dismissedRecently() {
    const v = localStorage.getItem(DISMISS_KEY);
    if (!v) return false;
    return Number(v) > Date.now();
}

function markDismissed() {
    localStorage.setItem(
        DISMISS_KEY,
        String(Date.now() + DISMISS_DAYS * 24 * 3600 * 1000)
    );
}

function isStandalone() {
    // Already-installed PWA — never show the prompt.
    return (
        window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator.standalone === true
    );
}

function isIos() {
    const ua = window.navigator.userAgent || "";
    return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

export default function InstallAppPrompt() {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [show, setShow] = useState(false);
    const [iosTip, setIosTip] = useState(false);

    useEffect(() => {
        if (isStandalone() || dismissedRecently()) return;
        if (ROUTE_HIDDEN.includes(window.location.pathname)) return;

        const onBeforeInstall = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            // Wait a beat so it never lands in the middle of the user
            // reading the landing hero.
            setTimeout(() => setShow(true), 6000);
        };
        window.addEventListener("beforeinstallprompt", onBeforeInstall);

        // iOS fallback — show the manual tip after ~12 seconds on the page.
        let iosTimer;
        if (isIos()) {
            iosTimer = setTimeout(() => setIosTip(true), 12000);
        }

        // Clear UI as soon as Chrome confirms the install completed.
        const onInstalled = () => {
            setShow(false);
            setIosTip(false);
            markDismissed();
        };
        window.addEventListener("appinstalled", onInstalled);

        return () => {
            window.removeEventListener("beforeinstallprompt", onBeforeInstall);
            window.removeEventListener("appinstalled", onInstalled);
            if (iosTimer) clearTimeout(iosTimer);
        };
    }, []);

    const dismiss = () => {
        markDismissed();
        setShow(false);
        setIosTip(false);
    };

    const triggerInstall = async () => {
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        const choice = await deferredPrompt.userChoice;
        if (choice?.outcome === "accepted") {
            markDismissed();
        }
        setDeferredPrompt(null);
        setShow(false);
    };

    if (!show && !iosTip) return null;

    return (
        <div
            data-testid="install-app-prompt"
            className="fixed left-4 bottom-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-[60] animate-fade-in-up"
        >
            <div className="ml-card p-4 bg-white shadow-xl border border-teal-soft/80 flex items-start gap-3">
                <span className="w-10 h-10 rounded-2xl bg-teal-soft text-teal-dark flex items-center justify-center shrink-0">
                    <Download size={18} strokeWidth={1.8} />
                </span>
                <div className="flex-1 min-w-0">
                    <p className="font-heading font-medium text-[#1F2624] text-sm md:text-base">
                        {iosTip
                            ? "Add Memory Lane to your Home Screen"
                            : "Install Memory Lane on your phone"}
                    </p>
                    <p className="text-xs text-[#4C5551] leading-relaxed mt-1">
                        {iosTip
                            ? "Tap the Share button in Safari, then choose “Add to Home Screen.” It opens like a real app — no App Store needed."
                            : "Get a one-tap shortcut on your home screen. Works offline-friendly, opens like a real app."}
                    </p>
                    {!iosTip && (
                        <button
                            onClick={triggerInstall}
                            data-testid="install-app-cta"
                            className="ml-btn-primary !min-h-[40px] mt-3 !px-4 text-sm inline-flex items-center gap-1.5"
                        >
                            <Download size={14} strokeWidth={1.8} />
                            Install
                        </button>
                    )}
                </div>
                <button
                    onClick={dismiss}
                    data-testid="install-app-dismiss"
                    aria-label="Dismiss"
                    className="text-[#6E7A74] hover:text-[#4C5551] p-1 -mr-1 -mt-1 shrink-0"
                >
                    <X size={16} strokeWidth={1.8} />
                </button>
            </div>
        </div>
    );
}
